/**
 * ⚡ Turbo PaketPilot (Experiment, je Gerät) — normales Erfassen per Foto ohne Prüfschritt.
 *
 * Idee (Philipp, 23.10.2026): Foto machen → Kamera bleibt offen → nächstes Foto. Erkennung (Gemini), Anlegen und
 * Etikett laufen im Hintergrund. Geht nur bei Sortierung nach Nachname: das Fach folgt dann aus dem Namen, es gibt
 * nichts einzutippen. Geprüft wird nur noch über das gedruckte Etikett.
 *  - Etiketten drucken streng in Foto-Reihenfolge (Erkennung parallel, Anlegen + Druck nacheinander), damit das
 *    Etikett zum Paket im Stapel passt; dazu die letzten 4 Ziffern der Sendungsnummer aufs Etikett.
 *  - Unsicher (kein Name, Name unsicher, Retoure, kein Fach, Erkennung gescheitert): Paket wird trotzdem angelegt,
 *    Fach „?" und „?" auf dem Etikett — die Reihe bricht nicht ab (Entscheidung Philipp 23.10.2026). Nacharbeiten
 *    über die Liste am Ende bzw. Fach „?" in der Paketliste.
 *  - Schon erfasst (gleiche Sendungsnummer offen im Bestand): nicht anlegen, kein Etikett.
 * Das bisherige „Turbo" für Vinted/DHL (services/turbo.js) heißt jetzt „Turbo Vinted/DHL".
 */
import { getCarrierSortMode, getExplicitCarrierSort, isAutoRegalLetter, extractHausnummer, isForwardingAddress } from './printerService';
import { autoRegalFach } from './regalUnterfach';
import { vintedFach, vintedCodeAusTracking, platzhalterCode, glsInVintedCode } from './vintedNames';
import { dpdAktiv, bekannterDpdName, dpdCodeAusTracking, istDpdLangBarcode } from './dpd';
import { findeGleichenEmpfaenger, findeGleicheSendung } from '../utils/packageMatchingHelpers';
import { hasCustomFeature } from './customFeatures';
import { isCaptureSenderOn } from './captureOptions';

const KEY = 'kiosk_turbo_pp';
export const isTurboPpOn = () => { try { return localStorage.getItem(KEY) === '1'; } catch { return false; } };
export const setTurboPpOn = (on) => { try { localStorage.setItem(KEY, on ? '1' : '0'); } catch { /* */ } };
/** Schalter sichtbar: Konto mit Nachname-Sortierung (z. B. Green Goat) */
export const turboPpVerfuegbar = () => hasCustomFeature('nachnameSortierung');
/** Wirklich aktiv: Schalter an UND auf diesem Gerät wird nach Nachname einsortiert */
export const turboPpAktiv = () => turboPpVerfuegbar() && isTurboPpOn() && isAutoRegalLetter();

// ⚙️ Anpassbare Optionen für den Turbo PaketPilot (je Gerät in localStorage)
const HUD_KEY = 'kiosk_turbo_hud'; // Standard: an ('0' = aus)
const AUTOFOTO_KEY = 'kiosk_turbo_autofoto'; // Standard: aus ('1' = an)
const REGAL_KEY = 'kiosk_turbo_regal'; // Standard: an ('0' = aus)
const TON_KEY = 'kiosk_turbo_ton'; // Standard: an ('0' = aus)

export const isTurboHudOn = () => { try { return localStorage.getItem(HUD_KEY) !== '0'; } catch { return true; } };
export const setTurboHudOn = (on) => {
  try {
    localStorage.setItem(HUD_KEY, on ? '1' : '0');
    if (!on) localStorage.setItem(AUTOFOTO_KEY, '0');
  } catch { /* */ }
};
export const isTurboAutoFotoOn = () => { try { return localStorage.getItem(AUTOFOTO_KEY) === '1'; } catch { return false; } };
export const setTurboAutoFotoOn = (on) => {
  try {
    localStorage.setItem(AUTOFOTO_KEY, on ? '1' : '0');
    if (on) localStorage.setItem(HUD_KEY, '1');
  } catch { /* */ }
};
export const isTurboRegalOn = () => { try { return localStorage.getItem(REGAL_KEY) !== '0'; } catch { return true; } };
export const setTurboRegalOn = (on) => { try { localStorage.setItem(REGAL_KEY, on ? '1' : '0'); } catch { /* */ } };
export const isTurboTonOn = () => { try { return localStorage.getItem(TON_KEY) !== '0'; } catch { return true; } };
export const setTurboTonOn = (on) => { try { localStorage.setItem(TON_KEY, on ? '1' : '0'); } catch { /* */ } };

/** Ist der Name nur ein Platzhalter („Vinted 123-456", „DHL 123-456" oder „DPD 123-456") statt eines echten Kundennamens? */
export function istPlatzhalterName(name) {
  const n = String(name || '').trim();
  return !!(platzhalterCode(n) || /^(DHL|DPD) [A-Z0-9]{3}-?[A-Z0-9]{3}$/i.test(n));
}

/**
 * 🚚 Gemeinsame Paketdienst-Erkennung (Handy-Betrieb NewPackageModal + Bench-Station + Turbo):
 * wertet Logo/Text (metadata.paketdienst), Sendungsnummer und Barcodes einheitlich aus.
 */
export function erkennePaketdienst({ metadata, ocrResult, barcodes = [], tracking = '' } = {}) {
  let erkannterDienst = String(metadata?.paketdienst || '').trim();
  const rawTrk = String(tracking || ocrResult?.tracking || metadata?.trackingNummer || '')
    .trim()
    .replace(/^.*?\b(air)?waybill[:\s#-]*\s*/i, '');
  const trkDigits = rawTrk.replace(/\D/g, '');
  const istReinZiffern = Boolean(trkDigits && trkDigits === rawTrk.replace(/[\s-]+/g, ''));
  const hat2L = (Array.isArray(barcodes) ? barcodes : []).some((b) =>
    /^(\]C1)?\(?2L\)?[A-Z]{2}[0-9A-Z]{3,10}\+/i.test(String(b?.text || b?.value || b || ''))
  );
  const istWaybill10 = /^\d{2}\s+\d{4}\s+\d{4}$/.test(rawTrk)
    || (istReinZiffern && trkDigits.length === 10 && Number(trkDigits.slice(0, 9)) % 7 === Number(trkDigits[9]));
  const istFedex12 = istReinZiffern && trkDigits.length === 12;
  const trkAlphaNum = rawTrk.replace(/[^A-Z0-9]/gi, '').toUpperCase();
  if (istReinZiffern && /^\d{10}0101\d{12}$/.test(trkDigits) && !/^(mondial\s*relay|inpost)$/i.test(erkannterDienst)) {
    erkannterDienst = 'Mondial Relay';
  } else if ((/^003404\d{14}$/.test(trkDigits) || /^3299\d{20}$/.test(trkDigits) || /^JVGL[A-Z0-9]{6,}$/.test(trkAlphaNum)) && !/^dhl/i.test(erkannterDienst)) {
    erkannterDienst = 'DHL';
  } else if (/^1Z[A-Z0-9]{16}$/.test(trkAlphaNum) && !/^ups$/i.test(erkannterDienst)) {
    erkannterDienst = 'UPS';
  } else if (/^(17\d{14}|VGS\d{13})$/.test(trkAlphaNum) && !/^vinted/i.test(erkannterDienst)) {
    erkannterDienst = 'Vinted Go';
  } else if (istFedex12 && /^dhl\s+express$/i.test(erkannterDienst)) {
    erkannterDienst = 'FedEx';
  } else if ((istWaybill10 || hat2L) && (!erkannterDienst || /^dhl(\s+express)?$/i.test(erkannterDienst))) {
    erkannterDienst = 'DHL Express';
  } else if (!erkannterDienst && /^3S[A-Z0-9]{9,}$/.test(trkAlphaNum)) {
    erkannterDienst = 'PostNL';
  } else if (!erkannterDienst && dpdCodeAusTracking(rawTrk) && (
    bekannterDpdName(rawTrk)
    || istDpdLangBarcode(rawTrk)
    || (Array.isArray(barcodes) && barcodes.some((b) => istDpdLangBarcode(String(b?.text || b?.value || b || '').trim())))
  )) {
    erkannterDienst = 'DPD';
  }
  return erkannterDienst;
}

/**
 * Fach wie im normalen Formular (NewPackageModal, Effekt „Fach-Vorschlag"): gleicher Empfänger schon im Regal →
 * dazulegen; sonst je nach Paketdienst Nachname-Buchstabe (mit Unterfach), Vinted-Kurznummer (V0–V9) oder Hausnummer.
 */
export function fachFuer({ name, streetAndNumber, trackingNumber, paketdienst }, pakete) {
  if (!name) return '';
  const autoLetter = isAutoRegalLetter();
  const carrierSort = getCarrierSortMode(paketdienst || '', trackingNumber, autoLetter);
  const explicitSort = getExplicitCarrierSort(paketdienst || '', trackingNumber);
  const istPh = istPlatzhalterName(name);
  let gleich = null;
  if (!istPh) {
    try { gleich = findeGleichenEmpfaenger(pakete, { name, streetAndNumber, id: null }); } catch { gleich = null; }
  }
  const gruppe = gleich && gleich.sicher ? String(gleich.regalplatz || '') : '';
  const passt = (fach) => {
    const brief = fach.charAt(0);
    return gruppe && (!brief || (fach.length > 1 ? gruppe.toUpperCase() === fach.toUpperCase() : gruppe.toUpperCase().startsWith(brief)));
  };
  if (carrierSort === 'kurznummer') {
    const vFach = vintedFach(vintedCodeAusTracking(trackingNumber) || platzhalterCode(name) || glsInVintedCode(trackingNumber, paketdienst)) || '';
    return (passt(vFach) ? gruppe : '') || vFach;
  }
  if (carrierSort === 'nachname') {
    if (istPh) return '';
    const fach = (autoLetter || explicitSort === 'nachname') ? autoRegalFach(name) : '';
    return (passt(fach) ? gruppe : '') || fach;
  }
  // Hausnummer (Dienst-Ausnahme wie PostNL): nur wenn nicht mehrere Empfänger unter derselben Adresse wohnen
  let hn = '';
  if ((explicitSort === 'hausnummer' || autoLetter) && streetAndNumber && !isForwardingAddress(streetAndNumber)) {
    const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
    const eigene = norm(streetAndNumber);
    const empf = new Set((pakete || []).filter((p) => p && !p.deleted && !p.ausgegebenAm && !p.zurueckgeschickt && norm(p.streetAndNumber) === eigene)
      .map((p) => String(p.name || '').trim().toLowerCase()).filter(Boolean));
    if (empf.size < 3) hn = extractHausnummer(streetAndNumber).toUpperCase();
  }
  return gruppe || hn;
}

/**
 * Unscharf? Schärfe = Laplace-Varianz auf einem 200-px-Graubild (InAppCamera). Nur als Begründung genutzt, wenn die
 * Erkennung ohnehin nichts Brauchbares fand — ein falscher Treffer ändert also nichts am Ablauf.
 */
export const SCHAERFE_GRENZE = 30;
export const istUnscharf = (schaerfe) => Number.isFinite(schaerfe) && schaerfe < SCHAERFE_GRENZE;

/**
 * 🎯 Live-Etiketten-Detektor für den Kamera-Stream (~1 ms auf 160×120 Pixeln).
 * Sucht das helle Versandetikett mit dunklen Text-/Barcode-Kanten im Videobild,
 * misst die lokale Schärfe (Laplace-Varianz im Etikett) und die Bewegung zum Vorbild.
 * → { gefunden, x, y, w, h, flaeche, schaerfe, motion, status: 'suche'|'nah'|'fokus'|'bereit', gray }
 */
export function erkenneEtikettBox(rgba, w, h, prevGray = null) {
  const n = w * h;
  const gray = new Float32Array(n);
  let sumLum = 0;
  let sumDiff = 0;
  const hatPrev = prevGray && prevGray.length === n;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const g = 0.299 * rgba[o] + 0.587 * rgba[o + 1] + 0.114 * rgba[o + 2];
    gray[i] = g;
    sumLum += g;
    if (hatPrev) sumDiff += Math.abs(g - prevGray[i]);
  }
  const meanLum = n ? sumLum / n : 128;
  const motion = hatPrev && n ? sumDiff / n : 0;

  const cw = 8;
  const ch = 8;
  const cols = Math.max(1, Math.floor(w / cw));
  const rows = Math.max(1, Math.floor(h / ch));
  const cellLum = new Float32Array(cols * rows);
  const cellGrad = new Float32Array(cols * rows);

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      let lSum = 0;
      let gSum = 0;
      let cnt = 0;
      const y0 = r * ch;
      const x0 = c * cw;
      for (let y = y0; y < y0 + ch - 1; y++) {
        const rowOff = y * w;
        for (let x = x0; x < x0 + cw - 1; x++) {
          const p = rowOff + x;
          const v = gray[p];
          lSum += v;
          gSum += Math.abs(v - gray[p + 1]) + Math.abs(v - gray[p + w]);
          cnt++;
        }
      }
      const idx = r * cols + c;
      cellLum[idx] = cnt ? lSum / cnt : 0;
      cellGrad[idx] = cnt ? gSum / cnt : 0;
    }
  }

  // Kontinuierliche Helligkeitsschwelle ohne Sprung (gedeckelt bei 185, damit weiße Pakete stabil bleiben)
  const lumSchwelle = Math.max(130, Math.min(185, meanLum + 12));
  let kernZellen = 0;
  let cMin = cols, cMax = -1, rMin = rows, rMax = -1;
  // Äußersten Rand (1 Zelle) ignorieren, damit Tischkanten den Rahmen nicht aufziehen
  for (let r = 1; r < rows - 1; r++) {
    for (let c = 1; c < cols - 1; c++) {
      const idx = r * cols + c;
      if (cellLum[idx] >= lumSchwelle && cellGrad[idx] >= 9) {
        kernZellen++;
        if (c < cMin) cMin = c;
        if (c > cMax) cMax = c;
        if (r < rMin) rMin = r;
        if (r > rMax) rMax = r;
      }
    }
  }

  const kernBoxZellen = (cMax - cMin + 1) * (rMax - rMin + 1);
  // Mindestens 5 Text-/Barcode-Zellen und eine zusammenhängende Fläche (≥ 28 % Dichte), sonst kein Etikett
  if (kernZellen < 5 || cMax < cMin || rMax < rMin || kernZellen < kernBoxZellen * 0.28) {
    return { gefunden: false, x: 0.18, y: 0.16, w: 0.64, h: 0.68, flaeche: 0, schaerfe: 0, motion, status: 'suche', gray };
  }

  // Weißen Etikettrand rund um den Text-/Barcode-Kern mitnehmen (höchstens 2 Zellen je Seite, damit weiße Tüten nicht das ganze Bild füllen)
  for (let s = 0; s < 2 && cMin > 1; s++) {
    let hell = 0;
    for (let r = rMin; r <= rMax; r++) if (cellLum[r * cols + (cMin - 1)] >= lumSchwelle) hell++;
    if (hell >= (rMax - rMin + 1) * 0.55) cMin--; else break;
  }
  for (let s = 0; s < 2 && cMax < cols - 2; s++) {
    let hell = 0;
    for (let r = rMin; r <= rMax; r++) if (cellLum[r * cols + (cMax + 1)] >= lumSchwelle) hell++;
    if (hell >= (rMax - rMin + 1) * 0.55) cMax++; else break;
  }
  for (let s = 0; s < 2 && rMin > 1; s++) {
    let hell = 0;
    for (let c = cMin; c <= cMax; c++) if (cellLum[(rMin - 1) * cols + c] >= lumSchwelle) hell++;
    if (hell >= (cMax - cMin + 1) * 0.55) rMin--; else break;
  }
  for (let s = 0; s < 2 && rMax < rows - 2; s++) {
    let hell = 0;
    for (let c = cMin; c <= cMax; c++) if (cellLum[(rMax + 1) * cols + c] >= lumSchwelle) hell++;
    if (hell >= (cMax - cMin + 1) * 0.55) rMax++; else break;
  }

  const bx = Math.max(0.04, cMin / cols);
  const by = Math.max(0.04, rMin / rows);
  const bw = Math.min(0.92 - bx, (cMax - cMin + 1) / cols);
  const bh = Math.min(0.92 - by, (rMax - rMin + 1) / rows);
  const flaeche = bw * bh;

  // Schärfe (Laplace-Varianz) gezielt IM erkannten Etikett-Rechteck messen
  const px0 = Math.max(1, cMin * cw);
  const py0 = Math.max(1, rMin * ch);
  const px1 = Math.min(w - 2, (cMax + 1) * cw);
  const py1 = Math.min(h - 2, (rMax + 1) * ch);
  let lSum = 0, lSum2 = 0, lCnt = 0;
  for (let y = py0; y <= py1; y++) {
    const off = y * w;
    for (let x = px0; x <= px1; x++) {
      const i = off + x;
      const l = gray[i - 1] + gray[i + 1] + gray[i - w] + gray[i + w] - 4 * gray[i];
      lSum += l;
      lSum2 += l * l;
      lCnt++;
    }
  }
  const schaerfe = lCnt ? lSum2 / lCnt - (lSum / lCnt) ** 2 : 0;

  let status = 'bereit';
  if (flaeche < 0.085) status = 'nah';
  else if (motion > 11 || schaerfe < 22) status = 'fokus';

  return { gefunden: true, x: bx, y: by, w: bw, h: bh, flaeche, schaerfe, motion, status, gray };
}

// Absender als eine Zeile wie im normalen Formular (NewPackageModal formatAbsenderObj)
const absenderText = (a) => {
  if (!a) return '';
  if (typeof a === 'string') return a.trim().slice(0, 250);
  const plzOrt = [a.postal, a.city].map((x) => String(x || '').trim()).filter(Boolean).join(' ');
  return [a.name, a.street, plzOrt].map((x) => String(x || '').trim()).filter(Boolean).join(' · ').slice(0, 250);
};

/** Letzte 4 Ziffern/Zeichen der Sendungsnummer fürs Etikett („#1234") */
export const kurzNr = (tracking) => {
  const t = String(tracking || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return t.length >= 4 ? t.slice(-4) : '';
};

/**
 * Erkennungsergebnis (mlService.analyzeImage) → Paketfelder + Bewertung. Reine Funktion.
 * → { felder: {name, streetAndNumber, postalAndCity, trackingNumber, paketdienst, imageUrl, absender?}, fach, unsicher, grund, doppelt }
 */
export function auswerten(result, pakete) {
  if (!result || result.fallback || result.error || !result.ocrResult) {
    return { felder: { name: '', streetAndNumber: '', postalAndCity: '', trackingNumber: '', paketdienst: '', imageUrl: (result && result.imageId) || '' }, fach: '', unsicher: true, grund: 'erkennung', doppelt: false };
  }
  const o = result.ocrResult || {};
  const m = result.metadata || {};
  const felder = {
    name: String(o.name || '').trim(),
    streetAndNumber: String(o.street || '').trim(),
    postalAndCity: [o.postal, o.city].filter(Boolean).join(' ').trim(),
    trackingNumber: String(o.tracking || m.trackingNummer || '').trim(),
    paketdienst: String(m.paketdienst || '').trim(),
    imageUrl: result.imageId || '',
    // Farbe/Gewicht liefert Gemini ohnehin mit (wie im normalen Formular, Farbe klein für die Farbkugeln)
    paketFarbe: String(m.paketFarbe || o.paketFarbe || '').toLowerCase().trim(),
    gewicht: String(m.gewicht || o.weight || '').trim(),
    // Absender nur mit eingeschalteter Option „Absender speichern" — personenbezogen (wie NewPackageModal)
    ...(isCaptureSenderOn() && absenderText(result.absender) ? { absender: absenderText(result.absender) } : {}),
  };
  // 📦 DPD Vorab-Name: Falls DPD aktiv ist und ein DPD-Barcode erkannt wurde,
  // fehlenden oder unsicheren OCR-Namen sofort durch den echten DPD-Empfängernamen ersetzen.
  if (dpdAktiv()) {
    const rawBcs = [felder.trackingNumber, ...(Array.isArray(result.barcodes) ? result.barcodes.map((b) => b?.text || b?.value || b) : [])];
    const hatDpdLangBc = rawBcs.some((raw) => istDpdLangBarcode(raw));
    const dpdCode = rawBcs.map((raw) => dpdCodeAusTracking(raw)).find(Boolean) || '';
    if (dpdCode) {
      const dpdName = bekannterDpdName(dpdCode);
      const istSicherDpd = Boolean(dpdName || hatDpdLangBc || /^dpd$/i.test(m.paketdienst || ''));
      if (dpdName && (!felder.name || result.nameUnsicher || result.etikettKlein)) {
        felder.name = dpdName;
      }
      if (istSicherDpd && (!felder.trackingNumber || hatDpdLangBc)) {
        felder.trackingNumber = dpdCode;
      }
      if (!felder.paketdienst && istSicherDpd) {
        felder.paketdienst = 'DPD';
      }
    }
  }
  let doppelt = false;
  // Shop-/Routen-Kennungen statt Sendungsnummer (z. B. „NL-DPD-0521-NLOOH" stand bei 9 verschiedenen Empfängern) zählen
  // nicht für „schon erfasst" — sonst gälten verschiedene Pakete als doppelt
  const nr = felder.trackingNumber || '';
  const shopKennung = /^[A-Z]{2}-[A-Z]{2,5}-\d{3,5}-/i.test(nr);
  const laengsteZiffernfolge = Math.max(0, ...(nr.match(/\d+/g) || []).map((z) => z.length));
  const echteNummer = !!nr && !shopKennung && laengsteZiffernfolge >= 6;
  try { doppelt = !!(echteNummer && findeGleicheSendung(pakete, { trackingNumber: felder.trackingNumber, id: null, paketdienst: felder.paketdienst })); } catch { doppelt = false; }
  const fach = fachFuer(felder, pakete);
  const grund = !felder.name ? 'keinName' : result.retoure ? 'retoure' : !fach ? 'keinFach' : '';
  // „Etikett klein im Foto" (Erkennungsdienst setzt dann nameUnsicher) ist im Turbo nur ein Hinweis, kein „?":
  // Folkerts erste Runde (24.10.2026) hatte 5 × „?" mit lauter richtig gelesenen Namen — im Turbo hält man das
  // Handy weiter weg, die Kamera fotografiert aber in voller Auflösung. Kontrolle bleibt das Etikett.
  const hinweis = !grund && (result.nameUnsicher || result.etikettKlein) ? 'etikettKlein' : '';
  return { felder, fach, unsicher: !!grund, grund, hinweis, doppelt };
}

const TEXTE = {
  de: {
    schalter: '⚡ Turbo PaketPilot (Experiment)',
    hinweis: 'Foto machen, die Kamera bleibt offen, nächstes Foto. Erkennung, Anlegen und Etikett laufen im Hintergrund. Nur bei Sortierung nach Nachname. Gilt nur auf diesem Gerät.',
    nurNachname: 'Geht nur, wenn auf diesem Gerät nach Nachname einsortiert wird (Einstellungen → Regal/Fach).',
    warnung: 'Im Turbo prüft niemand mehr das Ergebnis der Erkennung. Kontrolle nur noch über das gedruckte Etikett: Name und Fach mit dem Paket vergleichen. Unsichere Pakete bekommen Fach „?" und ein „?" auf dem Etikett.\n\nTurbo PaketPilot einschalten?',
    badge: '⚡ TURBO PaketPilot', erkannt: '{a}/{b} erkannt', fragen: '{n} × ?', doppelt: '{n} × schon erfasst',
    bereit: 'Bereit — Paket fotografieren', erkennt: 'wird erkannt …', wartet: 'Server kurz weg — wird nachgeholt',
    g_keinName: 'kein Name erkannt', g_retoure: 'Retoure?', g_nameUnsicher: 'Name unsicher', g_keinFach: 'kein Fach', g_erkennung: 'Erkennung gescheitert', g_doppelt: 'schon erfasst — kein Etikett', g_unscharf: 'Foto unscharf — Etikett mit ?',
    h_etikettKlein: 'Etikett klein im Foto — Namen auf dem Etikett prüfen',
    erfolgreich: '{a}/{b} ✓', schnitt: 'Ø {s} s', inArbeit: '{n} in Arbeit',
    beenden: 'Fertig', offen: 'Noch {n} Foto(s) in Arbeit. Trotzdem schließen? Sie laufen weiter, solange die App offen bleibt.',
    zusammenfassung: 'Turbo-Runde', pruefen: 'Bitte prüfen', bearbeiten: 'Bearbeiten', schliessen: 'Schließen', allesOk: 'Alles erkannt — nichts nachzuarbeiten.',
    fortsetzen: '{n} Foto(s) vom letzten Mal werden noch verarbeitet.',
    demo: 'Turbo-Ansicht ausprobieren (Demo, speichert nichts)',
    hud_suche: 'Etikett ins Bild halten',
    hud_nah: '🔍 Näher ran ans Etikett',
    hud_fokus: '⏳ Kurz ruhig halten …',
    hud_bereit: '✓ Etikett im Bild',
    autoFoto: '🤖 Auto-Foto',
    opt_hud: '🎯 Live-Etiketten-Rahmen',
    opt_hud_kurz: 'Abstands- & Bewegungs-Ampel im Kamerabild (aus = spart Akku)',
    opt_auto: '🤖 Auto-Foto',
    opt_auto_kurz: 'Löst von selbst aus, sobald das Etikett ruhig im Rahmen liegt',
    opt_regal: '🗄️ Regalkarte A–Z',
    opt_regal_kurz: 'Zeigt unten alle Fächer A–Z (aus = größeres Kamerabild)',
    opt_ton: '🔊 Signaltöne',
    opt_ton_kurz: 'Pieptöne bei Treffer, „?" und Doppelt (Vibration bleibt an)',
  },
  nl: {
    schalter: '⚡ Turbo PaketPilot (experiment)',
    hinweis: 'Foto maken, de camera blijft open, volgende foto. Herkenning, aanmaken en etiket gebeuren op de achtergrond. Alleen bij sorteren op achternaam. Geldt alleen op dit apparaat.',
    nurNachname: 'Werkt alleen als op dit apparaat op achternaam wordt ingeruimd (Instellingen → Schap/vak).',
    warnung: 'In Turbo controleert niemand meer het resultaat van de herkenning. Controle alleen nog via het geprinte etiket: naam en vak met het pakket vergelijken. Onzekere pakketten krijgen vak „?" en een „?" op het etiket.\n\nTurbo PaketPilot inschakelen?',
    badge: '⚡ TURBO PaketPilot', erkannt: '{a}/{b} herkend', fragen: '{n} × ?', doppelt: '{n} × al geregistreerd',
    bereit: 'Klaar — pakket fotograferen', erkennt: 'wordt herkend …', wartet: 'Server even weg — wordt ingehaald',
    g_keinName: 'geen naam herkend', g_retoure: 'Retour?', g_nameUnsicher: 'naam onzeker', g_keinFach: 'geen vak', g_erkennung: 'herkenning mislukt', g_doppelt: 'al geregistreerd — geen etiket', g_unscharf: 'foto onscherp — etiket met ?',
    h_etikettKlein: 'Etiket klein op de foto — naam op het etiket controleren',
    erfolgreich: '{a}/{b} ✓', schnitt: 'Ø {s} s', inArbeit: '{n} bezig',
    beenden: 'Klaar', offen: 'Nog {n} foto(\'s) in behandeling. Toch sluiten? Ze lopen door zolang de app open blijft.',
    zusammenfassung: 'Turbo-ronde', pruefen: 'Graag controleren', bearbeiten: 'Bewerken', schliessen: 'Sluiten', allesOk: 'Alles herkend — niets na te werken.',
    fortsetzen: '{n} foto(\'s) van de vorige keer worden nog verwerkt.',
    demo: 'Turbo-weergave uitproberen (demo, slaat niets op)',
    hud_suche: 'Houd etiket in beeld',
    hud_nah: '🔍 Dichter bij het etiket',
    hud_fokus: '⏳ Even stilhouden …',
    hud_bereit: '✓ Etiket in beeld',
    autoFoto: '🤖 Auto-foto',
    opt_hud: '🎯 Live-etiketkader',
    opt_hud_kurz: 'Afstands- & bewegingshulp in beeld (uit = spaart batterij)',
    opt_auto: '🤖 Auto-foto',
    opt_auto_kurz: 'Maakt automatisch een foto zodra het etiket stil in beeld is',
    opt_regal: '🗄️ Vakkenkaart A–Z',
    opt_regal_kurz: 'Toont onderaan alle vakken A–Z (uit = groter camerabeeld)',
    opt_ton: '🔊 Geluidssignalen',
    opt_ton_kurz: 'Pieptonen bij treffer, „?" en dubbel (trillen blijft aan)',
  },
  en: {
    schalter: '⚡ Turbo PaketPilot (experiment)',
    hinweis: 'Take a photo, the camera stays open, next photo. Recognition, creating and label run in the background. Only with sorting by surname. Applies to this device only.',
    nurNachname: 'Only works when this device sorts by surname (Settings → shelf/slot).',
    warnung: 'In Turbo nobody checks the recognition result any more. The only check is the printed label: compare name and slot with the parcel. Uncertain parcels get slot “?” and a “?” on the label.\n\nSwitch on Turbo PaketPilot?',
    badge: '⚡ TURBO PaketPilot', erkannt: '{a}/{b} recognised', fragen: '{n} × ?', doppelt: '{n} × already recorded',
    bereit: 'Ready — photograph a parcel', erkennt: 'recognising …', wartet: 'Server briefly away — will catch up',
    g_keinName: 'no name recognised', g_retoure: 'Return?', g_nameUnsicher: 'name uncertain', g_keinFach: 'no slot', g_erkennung: 'recognition failed', g_doppelt: 'already recorded — no label', g_unscharf: 'photo blurry — label with ?',
    h_etikettKlein: 'Label small in photo — check the name on the label',
    erfolgreich: '{a}/{b} ✓', schnitt: 'Ø {s} s', inArbeit: '{n} in progress',
    beenden: 'Done', offen: '{n} photo(s) still in progress. Close anyway? They continue while the app stays open.',
    zusammenfassung: 'Turbo round', pruefen: 'Please check', bearbeiten: 'Edit', schliessen: 'Close', allesOk: 'Everything recognised — nothing to fix.',
    fortsetzen: '{n} photo(s) from last time are still being processed.',
    demo: 'Try the Turbo view (demo, saves nothing)',
    hud_suche: 'Hold label in view',
    hud_nah: '🔍 Move closer to label',
    hud_fokus: '⏳ Hold steady …',
    hud_bereit: '✓ Label in frame',
    autoFoto: '🤖 Auto-photo',
    opt_hud: '🎯 Live label frame',
    opt_hud_kurz: 'Distance & motion guide in camera (off = saves battery)',
    opt_auto: '🤖 Auto-photo',
    opt_auto_kurz: 'Fires automatically once the label is held steady',
    opt_regal: '🗄️ Shelf map A–Z',
    opt_regal_kurz: 'Shows slots A–Z below (off = larger camera view)',
    opt_ton: '🔊 Sound signals',
    opt_ton_kurz: 'Beeps on match, “?” and duplicate (vibration stays on)',
  },
};

export function tpt(language) {
  const lang = TEXTE[language] ? language : 'en';
  return (key, vars) => {
    let s = TEXTE[lang][key] ?? TEXTE.de[key] ?? key;
    if (vars) for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, String(v));
    return s;
  };
}
